/**
 * DocsAdminPanel — IP-owner-only management surface for audience
 * grants on the docs portal.
 *
 * Lives on /docs (collapsed sidebar section) and surfaces:
 *
 *   - The platform-wide default-TTL setting (every new grant without
 *     an explicit `expires_at` gets `now + ttl_days`). One number,
 *     one PATCH, no migration.
 *   - The "expiring soon" list (next 14 days) so the IP-owner can
 *     renew BEFORE the daily sweep locks an auditor out.
 *   - The "active grants" table with one-click Revoke + Renew.
 *   - A "new grant" form with email, audience dropdown, date picker
 *     and reason.
 *
 * All API calls go through the existing `api` axios client, which
 * already pins the JWT bearer + REACT_APP_BACKEND_URL.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarClock,
  ShieldAlert,
  UserPlus,
  RefreshCw,
  Trash2,
  Settings2,
  ChevronDown,
  ChevronRight,
  Save,
} from "lucide-react";

const DEFAULT_NEW_GRANT = {
  email: "",
  audience: "auditor",
  expires_at: "",
  reason: "",
  permanent: false,
};

function isoToInputValue(iso) {
  // <input type="date"> expects YYYY-MM-DD.
  if (!iso) return "";
  return iso.slice(0, 10);
}

function inputValueToIso(date) {
  // Date-only input → end-of-day UTC so a "1 Aug expiry" means access
  // works through the whole of 1 Aug.
  if (!date) return null;
  return `${date}T23:59:59+00:00`;
}

function formatExpiresAt(iso) {
  if (!iso) return "permanent";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const days = Math.round((d.getTime() - Date.now()) / 86400000);
  const date = d.toISOString().slice(0, 10);
  if (days < 0) return `expired ${date}`;
  if (days === 0) return `today (${date})`;
  if (days === 1) return `tomorrow (${date})`;
  return `${days}d (${date})`;
}

export default function DocsAdminPanel({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [ttlInput, setTtlInput] = useState("");
  const [overrideDrafts, setOverrideDrafts] = useState({});
  const [savingTtl, setSavingTtl] = useState(false);
  const [grants, setGrants] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [permanentAudit, setPermanentAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newGrant, setNewGrant] = useState({ ...DEFAULT_NEW_GRANT });
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, g, e, a] = await Promise.all([
        api.get("/docs/settings"),
        api.get("/docs/grants?limit=200"),
        api.get("/docs/expiring-grants?within_days=14"),
        api.get("/docs/grant-audit?permanent_only=true&limit=50"),
      ]);
      setSettings(s.data);
      setTtlInput(String(s.data.default_ttl_days));
      setOverrideDrafts({ ...(s.data.ttl_overrides || {}) });
      setGrants(g.data.items || []);
      setExpiring(e.data.items || []);
      setPermanentAudit(a.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load admin panel");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !settings) reload();
  }, [open, settings, reload]);

  const onSaveTtl = async () => {
    const ttl = parseInt(ttlInput, 10);
    if (!Number.isInteger(ttl) || ttl < 1 || ttl > 365) {
      toast.error("Default TTL must be between 1 and 365 days.");
      return;
    }
    setSavingTtl(true);
    try {
      const { data } = await api.patch("/docs/settings", {
        default_ttl_days: ttl,
      });
      toast.success(`Default grant TTL set to ${data.default_ttl_days} days.`);
      setSettings({ ...settings, default_ttl_days: data.default_ttl_days });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingTtl(false);
    }
  };

  const onSaveOverride = async (audience, value) => {
    // Empty string / 0 → null (clears the override).
    const days = value === "" || value === "0"
      ? null
      : parseInt(value, 10);
    if (days !== null && (!Number.isInteger(days) || days < 1 || days > 365)) {
      toast.error(`${audience} TTL must be 1-365 days (or blank to clear).`);
      return;
    }
    try {
      const { data } = await api.patch("/docs/settings", {
        ttl_overrides: { [audience]: days },
      });
      setSettings({ ...settings, ttl_overrides: data.ttl_overrides });
      setOverrideDrafts({ ...data.ttl_overrides });
      toast.success(days === null
        ? `Cleared ${audience} override — falls back to default ${data.default_ttl_days}d.`
        : `${audience} TTL set to ${days} days.`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Override save failed");
    }
  };

  const onSubmitGrant = async (e) => {
    e.preventDefault();
    if (!newGrant.email || !newGrant.audience) {
      toast.error("Email + audience required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        email: newGrant.email.trim().toLowerCase(),
        audience: newGrant.audience,
        reason: newGrant.reason || undefined,
      };
      if (newGrant.permanent) {
        payload.permanent = true;
      } else if (newGrant.expires_at) {
        payload.expires_at = inputValueToIso(newGrant.expires_at);
      }
      const { data } = await api.post("/docs/grant", payload);
      toast.success(
        `Granted ${data.audience} to ${data.to} (expires ${formatExpiresAt(data.expires_at)}).`,
      );
      setNewGrant({ ...DEFAULT_NEW_GRANT });
      await reload();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Grant failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onRevoke = async (row) => {
    if (!window.confirm(`Revoke ${row.audience} from ${row.email}?`)) return;
    try {
      await api.post("/docs/revoke", { email: row.email, audience: row.audience });
      toast.success(`Revoked ${row.audience} from ${row.email}.`);
      await reload();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  const onRenew = async (row, days = 30) => {
    const next = new Date(Date.now() + days * 86400000)
      .toISOString()
      .slice(0, 10);
    try {
      const { data } = await api.post("/docs/grant", {
        email: row.email,
        audience: row.audience,
        expires_at: inputValueToIso(next),
        reason: `renewed ${days}d from admin panel`,
      });
      toast.success(
        `Renewed ${row.audience} for ${row.email} (now expires ${formatExpiresAt(data.expires_at)}).`,
      );
      await reload();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Renew failed");
    }
  };
  const onMarkReviewed = async (row) => {
    try {
      const payload = row.grant_audit_id
        ? { grant_audit_id: row.grant_audit_id }
        : { to: row.to, audience: row.audience, granted_at: row.granted_at };
      await api.post("/docs/grant-audit/review", payload);
      toast.success(
        `Marked ${row.audience} for ${row.to} reviewed. Re-justification clock reset.`,
      );
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mark reviewed failed");
    }
  };


  // ?renew=<email>|<audience> deep link from the weekly digest email.
  // When the IP-owner lands on /docs from a digest "Renew 30d →" link,
  // auto-expand the panel, fire the renew, then strip the query param
  // so a page refresh doesn't fire it again. Failures surface as toasts
  // — we never block the rest of the panel.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const renewParam = params.get("renew");
    if (!renewParam) return;
    const [email, audience] = renewParam.split("|");
    if (!email || !audience) return;
    setOpen(true);
    onRenew({ email, audience }, 30).finally(() => {
      params.delete("renew");
      const url = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}${window.location.hash}`;
      window.history.replaceState({}, "", url);
    });
  }, []);

  return (
    <div
      className="mt-5 border-t border-black/5 pt-3"
      data-testid="docs-admin-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-lg px-1 py-1 text-left text-[10px] font-semibold uppercase tracking-wider text-ek-purple hover:bg-ek-purple/5"
        data-testid="docs-admin-toggle"
        aria-expanded={open}
      >
        <span className="flex items-center gap-1.5">
          <ShieldAlert size={12} />
          Manage audience grants
        </span>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>

      {open && (
        <div className="mt-3 space-y-4 text-xs">
          {/* Default TTL setting */}
          <section
            className="rounded-lg border border-black/5 bg-ek-canvas p-3"
            data-testid="docs-admin-default-ttl"
          >
            <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-ek-ink/55">
              <Settings2 size={11} /> Default grant TTL
            </div>
            <p className="mb-2 text-[11px] leading-snug text-ek-ink/65">
              Every new grant without an explicit expiry will auto-expire
              after this many days. SOC 2 evidence: 100% of new grants
              are time-bounded.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={365}
                value={ttlInput}
                onChange={(e) => setTtlInput(e.target.value)}
                className="w-20 rounded border border-black/15 px-2 py-1 text-xs"
                data-testid="docs-admin-ttl-input"
              />
              <span className="text-[11px] text-ek-ink/55">days</span>
              <button
                type="button"
                onClick={onSaveTtl}
                disabled={savingTtl}
                className="ml-auto flex items-center gap-1 rounded-md bg-ek-purple px-2 py-1 text-[11px] font-semibold text-white hover:bg-ek-purple/90 disabled:opacity-50"
                data-testid="docs-admin-ttl-save"
              >
                <Save size={11} /> Save
              </button>
            </div>
          </section>

          {/* Per-audience TTL overrides */}
          <section
            className="rounded-lg border border-black/5 bg-ek-canvas p-3"
            data-testid="docs-admin-ttl-overrides"
          >
            <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-ek-ink/55">
              <Settings2 size={11} /> Per-audience TTL overrides
            </div>
            <p className="mb-2 text-[11px] leading-snug text-ek-ink/65">
              Match each audience to its risk profile. Blank = falls
              back to default {settings?.default_ttl_days || 30}d.
              e.g. regulator=90, internal=365.
            </p>
            <ul className="space-y-1">
              {(settings?.valid_audiences || [])
                .filter((a) => a !== "general" && a !== "ip-owner")
                .map((aud) => (
                  <li
                    key={aud}
                    className="flex items-center gap-2"
                    data-testid={`docs-admin-override-row-${aud}`}
                  >
                    <span className="w-28 truncate text-[11px] text-ek-ink/70">
                      {aud}
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={365}
                      placeholder="default"
                      value={overrideDrafts[aud] ?? ""}
                      onChange={(e) =>
                        setOverrideDrafts({
                          ...overrideDrafts,
                          [aud]: e.target.value,
                        })
                      }
                      className="w-16 rounded border border-black/15 px-2 py-0.5 text-[11px]"
                      data-testid={`docs-admin-override-input-${aud}`}
                    />
                    <span className="text-[10px] text-ek-ink/55">days</span>
                    <button
                      type="button"
                      onClick={() => onSaveOverride(aud, overrideDrafts[aud] ?? "")}
                      className="ml-auto rounded bg-ek-purple/10 px-2 py-0.5 text-[10px] font-semibold text-ek-purple hover:bg-ek-purple/20"
                      data-testid={`docs-admin-override-save-${aud}`}
                    >
                      Save
                    </button>
                  </li>
                ))}
            </ul>
          </section>

          {/* New grant form */}
          <section
            className="rounded-lg border border-black/5 bg-ek-canvas p-3"
            data-testid="docs-admin-new-grant"
          >
            <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-ek-ink/55">
              <UserPlus size={11} /> New grant
            </div>
            <form className="space-y-2" onSubmit={onSubmitGrant}>
              <input
                type="email"
                placeholder="user@company.com"
                value={newGrant.email}
                onChange={(e) =>
                  setNewGrant({ ...newGrant, email: e.target.value })
                }
                className="w-full rounded border border-black/15 px-2 py-1 text-xs"
                data-testid="docs-admin-new-email"
                required
              />
              <select
                value={newGrant.audience}
                onChange={(e) =>
                  setNewGrant({ ...newGrant, audience: e.target.value })
                }
                className="w-full rounded border border-black/15 px-2 py-1 text-xs"
                data-testid="docs-admin-new-audience"
              >
                {(settings?.valid_audiences || []).map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={newGrant.expires_at}
                  onChange={(e) =>
                    setNewGrant({ ...newGrant, expires_at: e.target.value })
                  }
                  disabled={newGrant.permanent}
                  className="flex-1 rounded border border-black/15 px-2 py-1 text-xs disabled:bg-black/5 disabled:text-ek-ink/40"
                  data-testid="docs-admin-new-expires"
                  min={new Date().toISOString().slice(0, 10)}
                />
                <span className="text-[10px] text-ek-ink/55">
                  blank = default {settings?.default_ttl_days || 30}d
                </span>
              </div>
              <label
                className="flex items-center gap-1.5 text-[10px] text-ek-ink/70"
                data-testid="docs-admin-new-permanent-label"
              >
                <input
                  type="checkbox"
                  checked={newGrant.permanent}
                  onChange={(e) =>
                    setNewGrant({ ...newGrant, permanent: e.target.checked })
                  }
                  data-testid="docs-admin-new-permanent"
                  className="h-3 w-3"
                />
                <span>
                  Permanent (no expiry) — <em>SOC 2 audit-flagged</em>
                </span>
              </label>
              <input
                type="text"
                placeholder="reason (audit log)"
                value={newGrant.reason}
                onChange={(e) =>
                  setNewGrant({ ...newGrant, reason: e.target.value })
                }
                className="w-full rounded border border-black/15 px-2 py-1 text-xs"
                data-testid="docs-admin-new-reason"
                maxLength={500}
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-ek-ink px-3 py-1.5 text-xs font-semibold text-white hover:bg-ek-ink/90 disabled:opacity-50"
                data-testid="docs-admin-new-submit"
              >
                {submitting ? "Granting…" : "Grant access"}
              </button>
            </form>
          </section>

          {/* Expiring soon */}
          <section
            className="rounded-lg border border-amber-200 bg-amber-50/40 p-3"
            data-testid="docs-admin-expiring"
          >
            <div className="mb-2 flex items-center justify-between gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
              <span className="flex items-center gap-1.5">
                <CalendarClock size={11} /> Expiring in 14 days
              </span>
              <span className="text-[10px] font-normal text-amber-700/70">
                {expiring.length}
              </span>
            </div>
            {loading ? (
              <p className="text-[11px] text-ek-ink/55">Loading…</p>
            ) : expiring.length === 0 ? (
              <p className="text-[11px] text-ek-ink/55">
                Nothing expiring in the next two weeks.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {expiring.map((g) => (
                  <li
                    key={`${g.email}-${g.audience}`}
                    className="flex items-center justify-between gap-2 rounded border border-amber-200/60 bg-white/70 px-2 py-1.5"
                    data-testid={`docs-admin-expiring-row-${g.audience}-${g.email}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[11px] font-medium text-ek-ink">
                        {g.email}
                      </p>
                      <p className="text-[10px] text-ek-ink/55">
                        {g.audience} · {g.hours_remaining}h left
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRenew(g, 30)}
                      className="rounded bg-ek-purple px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-ek-purple/90"
                      data-testid={`docs-admin-renew-${g.audience}-${g.email}`}
                    >
                      <RefreshCw size={10} className="inline" /> Renew 30d
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Active grants */}
          <section
            className="rounded-lg border border-black/5 bg-white p-3"
            data-testid="docs-admin-active-grants"
          >
            <div className="mb-2 flex items-center justify-between gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-ek-ink/55">
              <span>Active grants</span>
              <span className="text-[10px] font-normal text-ek-ink/45">
                {grants.length}
              </span>
            </div>
            {loading ? (
              <p className="text-[11px] text-ek-ink/55">Loading…</p>
            ) : grants.length === 0 ? (
              <p className="text-[11px] text-ek-ink/55">
                No explicit grants. Role-default audiences are not shown.
              </p>
            ) : (
              <ul className="max-h-64 space-y-1.5 overflow-y-auto">
                {grants.map((g) => (
                  <li
                    key={`${g.email}-${g.audience}-${g.granted_at}`}
                    className="rounded border border-black/5 px-2 py-1.5"
                    data-testid={`docs-admin-grant-row-${g.audience}-${g.email}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[11px] font-medium text-ek-ink">
                          {g.email}
                        </p>
                        <p className="text-[10px] text-ek-ink/55">
                          {g.audience} · {formatExpiresAt(g.expires_at)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => onRevoke(g)}
                        className="rounded bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100"
                        data-testid={`docs-admin-revoke-${g.audience}-${g.email}`}
                      >
                        <Trash2 size={10} className="inline" /> Revoke
                      </button>
                    </div>
                    {g.reason && (
                      <p className="mt-1 truncate text-[10px] italic text-ek-ink/45">
                        {g.reason}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* SOC 2 permanent-grants audit log */}
          <section
            className="rounded-lg border border-rose-200/60 bg-rose-50/40 p-3"
            data-testid="docs-admin-permanent-audit"
          >
            <div className="mb-2 flex items-center justify-between gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-rose-800">
              <span className="flex items-center gap-1.5">
                <ShieldAlert size={11} /> Permanent grants (SOC 2)
              </span>
              <span className="text-[10px] font-normal text-rose-700/70">
                {permanentAudit.length}
              </span>
            </div>
            <p className="mb-2 text-[10px] leading-snug text-rose-900/70">
              Every grant created with <em>permanent: true</em> shows up
              here. Cross-check before SOC 2 / ISO 27001 reviews to
              confirm each exemption is still justified.
            </p>
            {loading ? (
              <p className="text-[11px] text-ek-ink/55">Loading…</p>
            ) : permanentAudit.length === 0 ? (
              <p className="text-[11px] text-ek-ink/55">
                No permanent grants on record.
              </p>
            ) : (
              <ul className="max-h-64 space-y-1 overflow-y-auto">
                {permanentAudit.map((row, idx) => (
                  <li
                    key={`${row.grant_audit_id || row.to + "-" + row.audience + "-" + row.granted_at}-${idx}`}
                    className={`rounded border px-2 py-1.5 ${
                      row.review_overdue
                        ? "border-rose-400 bg-rose-100/60"
                        : "border-rose-200/40 bg-white/70"
                    }`}
                    data-testid={`docs-admin-permanent-row-${row.audience}-${row.to}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[11px] font-medium text-ek-ink">
                          {row.to}{" "}
                          <span className="font-normal text-ek-ink/55">
                            · {row.audience}
                          </span>
                        </p>
                        <p className="text-[10px] text-ek-ink/55">
                          Granted by {row.granted_by} ·{" "}
                          {(row.granted_at || "").slice(0, 10)}
                          {row.reason && (
                            <span className="ml-1 italic">— {row.reason}</span>
                          )}
                        </p>
                        <p
                          className="mt-0.5 text-[10px]"
                          data-testid={`docs-admin-permanent-review-${row.audience}-${row.to}`}
                        >
                          {row.review_overdue ? (
                            <span className="font-semibold text-rose-700">
                              ⚠ Overdue ({row.days_since_review}d since
                              {row.last_reviewed_at ? " review" : " grant"})
                            </span>
                          ) : row.last_reviewed_at ? (
                            <span className="text-emerald-700">
                              ✓ Reviewed {row.days_since_review}d ago by{" "}
                              {row.last_reviewed_by}
                            </span>
                          ) : (
                            <span className="text-ek-ink/55">
                              Never reviewed ·{" "}
                              {row.days_since_review ?? "?"}d since grant
                            </span>
                          )}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => onMarkReviewed(row)}
                        className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold ${
                          row.review_overdue
                            ? "bg-rose-600 text-white hover:bg-rose-700"
                            : "bg-ek-purple/10 text-ek-purple hover:bg-ek-purple/20"
                        }`}
                        data-testid={`docs-admin-mark-reviewed-${row.audience}-${row.to}`}
                      >
                        Mark reviewed
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
